import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import Layout from './components/Layout'
import Login from './pages/Login'
import Register from './pages/Register'
import Dashboard from './pages/Dashboard'
import Drones from './pages/Drones'
import DroneDetail from './pages/DroneDetail'
import Tasks from './pages/Tasks'
import Missions from './pages/Missions'
import MissionCreate from './pages/MissionCreate'
import MissionDetail from './pages/MissionDetail'
import Events from './pages/Events'
import Licenses from './pages/Licenses'
import Settings from './pages/Settings'
import UserManagement from './pages/UserManagement'
import UserDetail from './pages/UserDetail'
import CustomerSetup from './pages/CustomerSetup'
import CustomerSetupDetail from './pages/CustomerSetupDetail'
import OperationsLog from './pages/OperationsLog'
import { useAuthStore } from './stores/authStore'

const queryClient = new QueryClient()

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  return user ? <>{children}</> : <Navigate to="/login" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  const isAdmin = user.role === 'admin' || user.role === 'TenantAdmin'
  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>אין הרשאה</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    )
  }
  return <>{children}</>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />

              {/* Drones */}
              <Route path="drones" element={<Drones />} />
              <Route path="drones/:id" element={<DroneDetail />} />

              {/* Tasks hub — Deliveries moved here */}
              <Route path="tasks" element={<Tasks />} />

              {/* Deliveries kept as redirect for backward compat */}
              <Route path="deliveries" element={<Navigate to="/tasks" replace />} />

              {/* Missions */}
              <Route path="missions" element={<Missions />} />
              <Route path="missions/new" element={<MissionCreate />} />
              <Route path="missions/:id" element={<MissionDetail />} />

              {/* Events — route kept but not in nav (accessible via Dashboard) */}
              <Route path="events" element={<Events />} />

              {/* Settings — admin-gated */}
              <Route path="settings" element={<Settings />} />
              <Route path="settings/user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
              <Route path="settings/user-management/:id" element={<AdminRoute><UserDetail /></AdminRoute>} />
              <Route path="settings/customer-setup" element={<AdminRoute><CustomerSetup /></AdminRoute>} />
              <Route path="settings/customer-setup/:id" element={<AdminRoute><CustomerSetupDetail /></AdminRoute>} />
              <Route path="settings/licenses" element={<AdminRoute><Licenses /></AdminRoute>} />
              <Route path="settings/logs" element={<AdminRoute><OperationsLog /></AdminRoute>} />
              {/* /licenses → redirect to Settings > Licenses */}
              <Route path="licenses" element={<Navigate to="/settings/licenses" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </I18nextProvider>
    </QueryClientProvider>
  )
}

export default App
